import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import GoogleMapReact from 'google-map-react';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { platformSettingsService } from '../services/central-api/platform-settings';

export default function RouteMap({ cameraPositions, newZoom = 11, controlSize, zoomControl = false, useFullscreen = false }: { cameraPositions: CameraMap[]; newZoom?: number; controlSize?: number; zoomControl?: boolean; useFullscreen?: boolean; }) {
    const { styles } = useStyles(styleSheet);

    const [map, setMap] = useState<google.maps.Map>();
    const [maps] = useState<typeof google.maps>(window?.google?.maps);
    const [zoom, setZoom] = useState(11);
    const [defaultZoom, setDefaultZoom] = useState<number>(11);
    const [defaultCenter, setDefaultCenter] = useState<GoogleMapReact.Coords | undefined>();
    const { data } = platformSettingsService.useMapCentralPoint();

    useEffect(() => {
        if (newZoom && newZoom !== zoom) {
            setZoom(newZoom);
        }
    }, [newZoom]);

    function loadRouteInfo() {
        try {
            if (cameraPositions.length) {
                const directionsService = new google.maps.DirectionsService();
                const directionsRenderer = new google.maps.DirectionsRenderer({ map: map });
                directionsService.route(
                    {
                        origin: { lat: Number(cameraPositions[0].latitude), lng: Number(cameraPositions[0].longitude) },
                        destination: { lat: Number(cameraPositions[cameraPositions.length - 1].latitude), lng: Number(cameraPositions[cameraPositions.length - 1].longitude) },
                        waypoints: cameraPositions.slice(1, -1).map(position => ({
                            location: { lat: Number(position.latitude), lng: Number(position.longitude) },
                            stopover: true,
                        })),
                        travelMode: google.maps.TravelMode.DRIVING,
                    },
                    (response, status) => {
                        if (status === google.maps.DirectionsStatus.OK) {
                            directionsRenderer.setDirections(response);
                        } else {
                            console.error(`Directions request failed due to ${status}`);
                        }
                    }
                );
            } else if (defaultCenter && map) {
                map.setCenter(defaultCenter);
                map.setZoom(defaultZoom);
            }

        } catch (e) {
            console.error(e);
        }
    }

    useEffect(() => {
        if (data) {
            setDefaultCenter({ lat: data.latitude, lng: data.longitude });
            setDefaultZoom(data.zoom);
        }
    }, [data]);

    useEffect(loadRouteInfo, [map, maps, cameraPositions, defaultCenter]);

    return (
        <View style={styles.mapContainer}>
            {defaultCenter &&
                <GoogleMapReact
                    center={defaultCenter}
                    draggable={true}
                    defaultZoom={defaultZoom}
                    zoom={defaultZoom}
                    options={() => ({
                        streetViewControl: false,
                        fullscreenControl: useFullscreen,
                        controlSize: controlSize ?? undefined,
                        zoomControl: zoomControl,
                        styles: UnistylesRuntime.themeName === 'light' || UnistylesRuntime.themeName == 'lightAlt' ? [{
                            featureType: 'poi',
                            elementType: 'labels',
                            stylers: [{ visibility: 'off' }],
                        },
                        {
                            featureType: 'transit',
                            stylers: [{ visibility: 'off' }],
                        }] : [
                            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                            { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                            { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                            { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                            { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                            {
                                featureType: 'administrative.locality',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#d59563' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'geometry',
                                stylers: [{ color: '#38414e' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'geometry.stroke',
                                stylers: [{ color: '#212a37' }],
                            },
                            {
                                featureType: 'road',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#9ca5b3' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'geometry',
                                stylers: [{ color: '#746855' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'geometry.stroke',
                                stylers: [{ color: '#1f2835' }],
                            },
                            {
                                featureType: 'road.highway',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#f3d19c' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'geometry',
                                stylers: [{ color: '#17263c' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'labels.text.fill',
                                stylers: [{ color: '#515c6d' }],
                            },
                            {
                                featureType: 'water',
                                elementType: 'labels.text.stroke',
                                stylers: [{ color: '#17263c' }],
                            },
                        ]
                    })}
                    yesIWantToUseGoogleMapApiInternals
                    onGoogleApiLoaded={({ map }: { map: google.maps.Map; }) => {
                        setMap(map);
                    }}>
                </GoogleMapReact>
            }
        </View >
    );
}


const styleSheet = createStyleSheet(() => ({
    mapContainer: {
        flex: 1
    }
}));

